/**
 * Les deux natures de « ce qui m'attend ».
 *
 * La liste de travail rassemble des tâches de kanban, des étapes de processus et
 * des demandes de guichet.
 * Elles se lisent pareil — un titre, une provenance, une échéance — mais ne
 * s'ouvrent pas au même endroit et ne se numérotent pas dans la même table.
 * Ces quelques fonctions tiennent cette différence en un seul point, pour que
 * l'écran complet et l'encart du dashboard ne se mettent pas à en raconter deux
 * versions.
 */

import { teinteDe } from '@/utils/teintes'

/** Les étapes n'ont pas de couleur propre : le violet dit « processus ». */
const COULEUR_PROCESSUS = '#7D5BD6'

export function estEtape(ligne) {
    return ligne?.nature === 'etape'
}

export function estDemande(ligne) {
    return ligne?.nature === 'demande'
}

/** Une tâche de kanban : seule nature à avoir un projet et une colonne terminale. */
export function estCarte(ligne) {
    return !estEtape(ligne) && !estDemande(ligne)
}

/**
 * Clé de rendu. Les deux tables numérotent chacune les leurs : la tâche 12 et
 * l'étape 12 existent toutes les deux, et `:key="id"` les confondrait.
 */
export function cleLigne(ligne) {
    return `${ligne.nature ?? 'carte'}-${ligne.id}`
}

/**
 * La teinte d'une ligne, la même que celle de son projet dans la liste des
 * projets : on reconnaît d'où vient une tâche sans lire son nom de projet,
 * et c'est le même ton des deux côtés.
 */
export function couleurLigne(ligne) {
    if (estEtape(ligne)) return COULEUR_PROCESSUS

    return teinteDe(ligne.contexte?.libelle, ligne.contexte?.couleur)
}

/** D'où vient cette ligne : le nom du projet, ou celui du processus. */
export function provenance(ligne) {
    return ligne.contexte?.libelle || ''
}

/** Où elle en est : la colonne du kanban, ou ce sur quoi porte le dossier. */
export function precision(ligne) {
    return ligne.contexte?.detail || ''
}

/**
 * Là où la ligne s'ouvre : une tâche dans son projet, dépliée ; une étape dans
 * son dossier ; une demande dans sa fiche de ticket. Renvoie `null` quand la
 * cible manque, pour que l'appelant ne navigue pas dans le vide.
 */
export function destination(ligne) {
    if (estEtape(ligne)) {
        return ligne.dossier_id
            ? { path: `/processus/dossiers/${ligne.dossier_id}`, query: { etape: ligne.id } }
            : null
    }

    if (estDemande(ligne)) {
        return { path: `/support/tickets/${ligne.id}` }
    }

    return ligne.projet?.id
        ? { path: `/projets/tableaux/${ligne.projet.id}`, query: { carte: ligne.id } }
        : null
}
